/**
 * Bau und Prüfung einer Kalkulationsposition — geteilt zwischen
 * Angebotsmaske, Schnellkalkulation und Worker.
 *
 * Vorher stand die Regel „Pfand trägt keine Fracht" an zwei Stellen mit
 * einem Kommentar, der auf die jeweils andere verwies. Mit der
 * Schnellkalkulation wären es drei geworden — genau die Drift, die
 * `calc.ts` verhindern soll.
 */

import type { PositionInput, ZuschlagInput } from "./calc.js";

export interface ProduktAngaben {
  bezeichnung: string;
  warengruppe: string;
  basispreis_e4: number;
  ust_bp: number;
}

export interface VerpackungAngaben {
  bezeichnung: string;
  gebinde_kg: number | null;
  betrag_e4: number;
}

/**
 * Pfand ist Stückgut in einer Kette, die sonst durchweg in 100-kg-Schritten
 * rechnet. Der Basispreis eines Pfandartikels versteht sich deshalb je
 * Stück, und ein Stück entspricht rechnerisch 100 kg. Ohne diese Umrechnung
 * müsste der Sachbearbeiter für 20 Paletten „0,2" in ein Feld tippen, das
 * mit „Menge (t)" beschriftet ist.
 */
export const PFAND_KG_JE_STUECK = 100;

export const istStueckgut = (warengruppe: string) => warengruppe === "pfand";

export function stueckZuKg(stueck: number): number {
  return Math.round(stueck) * PFAND_KG_JE_STUECK;
}

export function kgZuStueck(kg: number): number {
  return Math.round(kg / PFAND_KG_JE_STUECK);
}

export function bauePositionInput(a: {
  produkt: ProduktAngaben;
  verpackung: VerpackungAngaben | null;
  zuschlaege: ZuschlagInput[];
  /** Frachtanteil der Lieferung; wird bei Pfand verworfen. */
  frachtE4: number;
  mengeKg: number;
  rabattBp: number;
}): PositionInput {
  return {
    bezeichnung: a.produkt.bezeichnung,
    verpackung_text: a.verpackung?.bezeichnung ?? "—",
    menge_kg: a.mengeKg,
    ust_bp: a.produkt.ust_bp,
    basispreis_e4: a.produkt.basispreis_e4,
    verpackung_e4: a.verpackung?.betrag_e4 ?? 0,
    zuschlaege: a.zuschlaege,
    // Pfand ist keine frachtpflichtige Ware: eine Palette bekommt weder
    // Frachtanteil noch die Gutschrift bei Selbstabholung.
    fracht_e4: istStueckgut(a.produkt.warengruppe) ? 0 : a.frachtE4,
    rabatt_bp: a.rabattBp,
  };
}

/**
 * Gibt eine Meldung zurück oder null. Dieselben Regeln, die der Worker beim
 * Speichern durchsetzt — damit die Maske den Knopf sperrt, statt den Nutzer
 * in einen Serverfehler laufen zu lassen.
 */
export function pruefePosition(a: {
  produkt: ProduktAngaben | null;
  verpackung: VerpackungAngaben | null;
  mengeKg: number;
  rabattBp: number;
}): string | null {
  if (!a.produkt) return "Kein Artikel gewählt.";

  /*
   * Ohne Listenpreis keine Position.
   *
   * `basispreis_e4` ist NOT NULL, ein fehlender Preis steht deshalb als 0 in
   * der Tabelle — und eine 0 laeuft ungehindert durch die ganze Kette. Der
   * teuerste Fall ist nicht der offensichtliche: Ein Artikel ohne Preis mit
   * Sack, Bio- und CCC-Zuschlag ergibt 9,85 €/100 kg statt 36,70 € und sieht
   * damit aus wie ein Preis. Bei loser Silolieferung, wo die Verpackung
   * einen ABSCHLAG traegt, kommt sogar ein negatives Angebot heraus.
   * Gemessen an echten Verpackungs- und Zuschlagswerten.
   */
  if (a.produkt.basispreis_e4 <= 0)
    return `Für ${a.produkt.bezeichnung} ist kein Listenpreis gepflegt — unter Stammdaten nachtragen.`;

  if (!Number.isInteger(a.mengeKg) || a.mengeKg <= 0) return "Menge fehlt.";
  if (a.mengeKg > 10_000_000) return "Menge unrealistisch hoch.";

  if (a.verpackung?.gebinde_kg && a.mengeKg % a.verpackung.gebinde_kg !== 0)
    return `${a.verpackung.bezeichnung} wird nur in Vielfachen von ${a.verpackung.gebinde_kg} kg geliefert.`;

  if (!Number.isInteger(a.rabattBp) || a.rabattBp < 0 || a.rabattBp > 10_000)
    return "Rabatt außerhalb 0–100 %.";

  return null;
}
